import { useTranslation } from 'react-i18next';

import { NgButtonNavLink } from '@/ui';
import { usePsapConfiguration } from '@/app/shell/psapConfiguration';
import type { GeoPoint, PermanentResidence, Position } from '@/signalr';
import { changeCallerLocationFromMap } from './actions';
import { createMapLinkModel } from './maps/externalMapLinks';
import { IncidentDetailMapComponent } from './maps/IncidentDetailMapComponent';
import { getMapConfiguration } from './model/psapConfiguration';

export type IncidentDetailMapProps = {
  incidentId: string;
  callerPosition: Position | null;
  callerPositionHistory: readonly Position[];
  permanentResidence?: PermanentResidence | null;
  incidentIsReadOnly: boolean;
};

export function IncidentDetailMap({
  incidentId,
  callerPosition,
  callerPositionHistory,
  permanentResidence,
  incidentIsReadOnly,
}: IncidentDetailMapProps) {
  const { t } = useTranslation();
  const psapConfiguration = usePsapConfiguration();
  const mapConfiguration = getMapConfiguration(psapConfiguration);

  const effectiveMapCenter: GeoPoint =
    callerPosition ?? permanentResidence?.geoPoint ?? mapConfiguration.defaultPosition;

  const effectiveZoom =
    callerPosition !== null ? (callerPosition.accuracy > 200 ? 14 : 17) : mapConfiguration.defaultZoom;

  const navigationTargetUrl =
    callerPosition !== null
      ? createMapLinkModel(mapConfiguration.navigationMapLink, callerPosition, {
          mapyCzAltText: t('Incident_InfoPanel_IconMapyCzAltText'),
          googleMapsAltText: t('Incident_InfoPanel_IconGoogleMapsAltText'),
        }).link
      : null;

  const changeCallerPositionFromMap = async (coordinates: GeoPoint) => {
    await changeCallerLocationFromMap(incidentId, coordinates);
  };

  return (
    <div className="h-100 position-relative">
      <IncidentDetailMapComponent
        incidentId={incidentId}
        center={effectiveMapCenter}
        zoom={effectiveZoom}
        callerPosition={callerPosition}
        callerPositionHistory={callerPositionHistory}
        permanentResidence={permanentResidence}
        onChangeCallerLocation={changeCallerPositionFromMap}
        incidentIsReadOnly={incidentIsReadOnly}
      />

      {navigationTargetUrl !== null && (
        <div className="d-tablet-none position-absolute bottom-0 start-0 end-0 d-flex gap-2 justify-content-center pb-2 z-2">
          <NgButtonNavLink icon="fa-light fa-route" text={t('Button_Navigate')} to={navigationTargetUrl} target="_blank" />
        </div>
      )}
    </div>
  );
}
